import { Link } from 'react-router-dom';
import type { UISelectionType } from '@components/types';
import type { ResourceState } from '@domain/schemas/contract.schema';
import type { DbTag } from '@domain/schemas/tag.schema';
import type { Team } from '@domain/schemas/team.schema';
import StatusBadge from '@pages/User/components/StatusBadge';
import type { TemplateShort } from '@queries/fetchTemplates';
import { getFormattedStatus, isNonNullish } from '@root/helpers';
import { isPPLFrontend } from '@root/src/utils/app-paths';
import { type CellContext, createColumnHelper } from '@tanstack/react-table';

import {
  classOfBusinessAccessor,
  createdAtAccessor,
  createdByAccessor,
  lastUpdatedByAccessor,
  tagsAccessor,
  teamsAccessor,
  updatedAtAccessor,
} from './shared';
import { brandingAccessor } from './submissions';
import { getBaseAccessor } from './templates';

const columnHelper = createColumnHelper<TemplateShort>();

export const getTemplatesTableColumns = ({
  brandingFieldName,
  brandingOptions,
  tags,
  templateStatuses,
  teams,
}: {
  brandingFieldName: 'branding' | 'branding_id';
  brandingOptions: UISelectionType[] | string[];
  tags: DbTag[];
  templateStatuses: string[];
  teams: Team[];
}) =>
  [
    columnHelper.accessor('id', {
      id: 'id',
      enableColumnFilter: false,
      enableHiding: false,
    }),
    columnHelper.accessor('ref', {
      id: 'ref',
      enableColumnFilter: false,
      header: 'Template ID',
      filterFn: 'arrIncludesSome',
      meta: {
        filter: {
          placeholder: 'All IDs',
        },
      },
      size: 144,
      maxSize: 144,
      sortingFn: 'text',
    }),
    columnHelper.accessor('name', {
      id: 'name',
      enableColumnFilter: false,
      header: 'Template Name',
      sortingFn: 'text',
      sortUndefined: -1,
      filterFn: 'arrIncludesSome',
      enableHiding: false,
      size: 200,
      meta: {
        filter: {
          label: 'Template name',
          placeholder: 'All templates',
        },
        headingCellClassName: 'truncate',
        tooltip: true,
      },
      cell: (cellContext: CellContext<TemplateShort, string>) => (
        <span className="truncate">{cellContext.getValue()}</span>
      ),
    }),
    getBaseAccessor<TemplateShort>(columnHelper),
    teamsAccessor<TemplateShort>(columnHelper, teams),
    columnHelper.accessor('status', {
      id: 'status',
      header: 'Status',
      filterFn: 'arrIncludesSome',
      size: 148,
      cell: (cellContext: CellContext<TemplateShort, ResourceState>) => (
        <StatusBadge className="mt-0" status={cellContext.getValue()} />
      ),
      meta: {
        filter: {
          placeholder: 'All statuses',
          selected: templateStatuses.filter((status) => status !== 'ARCHIVED'),
          options: templateStatuses.map((status) => ({
            name: getFormattedStatus(status),
            value: status,
          })),
        },
        dataCellClassName: '!p-0',
        headingLinkClassName: '!pl-0',
      },
    }),
    isPPLFrontend
      ? undefined
      : columnHelper.accessor((row) => (row.has_default_content ? 'yes' : 'no'), {
          id: 'has_default_content',
          header: 'Content',
          filterFn: 'arrIncludesSome',
          size: 140,
          cell: (cellContext: CellContext<TemplateShort, string>) => (
            <div className="inline-flex items-center">
              <span
                data-testid="cell:has_default_content"
                className="inline-flex items-center gap-x-1.5 rounded-xl bg-white px-3 py-1 text-xs font-medium text-gray-900 ring-1 ring-gray-200 ring-inset"
              >
                {cellContext.getValue() === 'yes' ? 'Populated' : 'None'}
              </span>
            </div>
          ),
          meta: {
            filter: {
              label: 'Contains Default Content?',
              placeholder: 'All templates',
              options: [
                { name: 'Populated', value: 'yes' },
                { name: 'None', value: 'no' },
              ],
            },
          },
        }),
    tagsAccessor<TemplateShort>(columnHelper, tags),
    lastUpdatedByAccessor<TemplateShort>(columnHelper),
    updatedAtAccessor(columnHelper),
    createdByAccessor(columnHelper),
    createdAtAccessor(columnHelper),
    brandingAccessor<TemplateShort>(columnHelper, brandingFieldName, brandingOptions),
    classOfBusinessAccessor<TemplateShort>(columnHelper),
    columnHelper.display({
      id: 'view',
      header: '',
      enableColumnFilter: false,
      enableHiding: false,
      size: 65,
      minSize: 65,
      maxSize: 65,
      cell: (cellContext: CellContext<TemplateShort, unknown>) => (
        <Link
          className="text-primary-600 hover:text-primary-900 font-light"
          target="_self"
          to={`/templates/${cellContext.row.getValue('id')}/contract`}
          rel="noreferrer"
        >
          View
        </Link>
      ),
      meta: {
        dataCellClassName: 'relative truncate whitespace-nowrap px-1 py-0 text-center text-sm font-medium',
      },
    }),
  ].filter(isNonNullish);
